import { Allotment } from 'allotment'
import { useEffect, useState } from 'react'

import { driversSelectors, fetchDrivers, saveDriver, toggleDriverStatus } from '@/entities/drivers'
import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { Button, Icon, IconButton, ToggleButton } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faAdd, faClose, faEdit, faKey, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Divider, Grid, styled } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { getDriverFormDefinition, initialDriverFormValues } from './libs/getDriverFormDefinition'
import {
  StyledAddButton, StyledButtonsContainer, StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane,
} from './StyledOutletPage'

export type DriversPageProps = {
  //
}

export const DriversPage: React.FC<DriversPageProps> = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit' | 'view' | 'reset-password'>('add')
  const [isSubmitting, setSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)

  const dispatch = useAppDispatch()
  const drivers = useAppSelector(state => driversSelectors.selectAll(state))
  const { data: runOptions } = useDropdown('runs')
  const { data: groupOptions } = useDropdown('groups')

  const { setToastContent } = useToastContext()

  // Fetch drivers from server
  const fetchDriversFromServer = async () => {
    setLoading(true)
    await dispatch(fetchDrivers())
    setLoading(false)
  }

  useEffect(() => {
    fetchDriversFromServer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Define the form fields and contents
  const formDefinition = getDriverFormDefinition({
    defaultValues: initialDriverFormValues,
    showPasswordFields: formType === 'add',
    isResetPassword: formType === 'reset-password',
    selectOptions: {
      runIds: runOptions,
      groupIds: groupOptions,
    },
  })

  const handleToggleStatus = async (id: string) => {
    try {
      await dispatch(toggleDriverStatus({ id }))
      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Driver status updated successfully',
      })
    } catch (error) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to update driver status',
      })
    }
  }

  const handleCloseDetail = () => {
    setRowSelectionModel(undefined)
    setShowDetails(false)
  }

  const onHandleResetPassword = () => {
    setFormType('reset-password')
    setFormValues({
      ...formValues,
      password: '',
      confirmPassword: '',
    })
  }

  const handleSubmit = (data: Record<string, unknown>) => {
    setSubmitting(true)

    const selectedDriver = drivers.find(driver => driver.id === rowSelectionModel)

    if (formType === 'edit' && selectedDriver) {
      // Handle edit driver
      const editPayload = {
        ...selectedDriver,
        ...data,
        groupIds: (data.groupIds as DropdownOption[]).map(g => g.value),
        runIds: (data.runIds as DropdownOption[]).map(r => r.value),
      }

      dispatch(saveDriver({
        driver: editPayload,
      })).then(() => {
        setFormType('view')
      })
    } else if (formType === 'reset-password' && selectedDriver) {
      // Handle reset password
      const resetPasswordPayload = {
        ...selectedDriver,
        password: data.password,
        confirmPassword: data.confirmPassword,
      }

      dispatch(saveDriver({
        driver: resetPasswordPayload,
      })).then(() => {
        setFormType('view')
        setToastContent({
          open: true,
          severity: 'success',
          title: 'Success',
          content: 'Driver password reset successfully',
        })
      })
    } else {
      // Handle add driver
      const addPayload = {
        ...data,
        groupIds: (data.groupIds as DropdownOption[]).map(g => g.value),
        runIds: (data.runIds as DropdownOption[]).map(r => r.value),
      }

      dispatch(saveDriver({
        driver: addPayload,
      })).then(() => {
        setFormType('view')
      })
    }

    setSubmitting(false)
  }

  // Update form values when selectedId changes
  const resetForm = () => {
    if (formType === 'add') {
      setFormValues(initialDriverFormValues)
    }
    else {
      const selectedDriver = drivers.find(driver => driver.id === rowSelectionModel)

      if (selectedDriver) {
        setFormValues({
          ...selectedDriver,
          groupIds: groupOptions.filter(g => selectedDriver.groupIds.includes(g.value)),
          runIds: runOptions.filter(r => selectedDriver.runIds.includes(r.value)),
        })
      }
    }
  }

  useEffect(() => {
    if (!rowSelectionModel || !['edit', 'view', 'reset-password'].includes(formType)) return

    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel, drivers, formType, setFormValues])

  // Define the columns and rows for the grid
  const rows = drivers.map(item => ({
    ...item,
    isSelectable: true,
  }))

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      filterable: true,
    },
    {
      field: 'login',
      headerName: 'Login',
      width: 150,
      filterable: true,
    },
    {
      field: 'groupIds',
      headerName: 'Groups',
      width: 200,
      filterable: true,
      valueGetter: (value: EntityId[]) => {
        return value
          .map(id => groupOptions.find(g => g.value === id)?.label)
          .filter(Boolean)
          .join(', ')
      },
    },
    {
      field: 'runIds',
      headerName: 'Runs',
      width: 200,
      filterable: true,
      valueGetter: (value: EntityId[]) => {
        return value
          .map(id => runOptions.find(r => r.value === id)?.label)
          .filter(Boolean)
          .join(', ')
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      filterable: true,
      valueGetter: (value) => value ? 'Active' : 'Disabled',
    },
  ]

  const onViewRowDetail = (row: GridValidRowModel) => {
    setFormType('view')
    setShowDetails(true)
    setRowSelectionModel(row.id)
  }

  const onHandleAdd = () => {
    setFormType('add')
    setShowDetails(true)
    setRowSelectionModel(undefined)
    setFormValues(initialDriverFormValues)
  }

  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Drivers</StyledHeader>
                <StyledDescription>
                  Manage driver profiles and assign them to specific runs.
                </StyledDescription>
              </Grid>
              <Grid item>
                <StyledAddButton
                  primary
                  startIcon={<Icon icon={faAdd} />}
                  onClick={onHandleAdd}
                  sx={{ minWidth: pxToRem(130) }}
                >
                  New Driver
                </StyledAddButton>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              loading={loading}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowClick={onViewRowDetail}
              initialState={{
                filter: {
                  filterModel: {
                    items: [{
                      field: 'isActive', operator: 'equals', value: 'Active',
                    }],
                  },
                },
              }}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              sx={{
                '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
                  {formType === 'add' && 'Add a new driver'}
                  {formType === 'edit' && `Edit ${formValues?.name || ''} details`}
                  {formType === 'view' && `View ${formValues?.name || ''} details`}
                  {formType === 'reset-password' && `Reset password for ${formValues?.name || ''}`}
                </StyledHeader>
              </Grid>
              <StyledButtonsContainer item>
                {formType === 'view' && (
                  <>
                    <ToggleButton
                      value="active"
                      primary
                      text
                      selected={false}
                      onChange={() => handleToggleStatus(rowSelectionModel as string)}
                    >
                      <Icon icon={faToggleOn} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      Toggle Status
                    </ToggleButton>
                    <Button
                      outlined
                      onClick={onHandleResetPassword}
                    >
                      <Icon icon={faKey} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      Reset Password
                    </Button>
                    <ToggleButton
                      value="checked"
                      primary
                      text
                      selected={false}
                      onChange={() => {
                        resetForm()
                        setFormType('edit')
                      }}
                      sx={{ minWidth: pxToRem(100) }}
                    >
                      <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      Edit Driver
                    </ToggleButton>
                  </>
                )}
                {(formType === 'edit' || formType === 'reset-password') && (
                  <ToggleButton
                    value="checked"
                    primary
                    text
                    selected={true}
                    onChange={() => {
                      resetForm()
                      setFormType('view')
                    }}
                    sx={{ minWidth: pxToRem(100) }}
                  >
                    <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                    Cancel
                  </ToggleButton>
                )}
                <IconButton size="small" onClick={() => handleCloseDetail()}>
                  <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
                </IconButton>
              </StyledButtonsContainer>
            </Grid>
            <DynamicForm
              {...formDefinition}
              initialValues={formValues}
              onSubmit={handleSubmit}
              isReadOnly={isSubmitting || !showDetails || formType === 'view'}
              onCancel={() => {
                resetForm()

                if (formType === 'add') {
                  setShowDetails(false)
                }

                setFormType('view')
              }}
              slotProps={{
                submitButton: {
                  disabled: isSubmitting,
                },
              }}
            />
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  height: calc(100% - ${pxToRem(86)});
`